import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../../services/api';
import GanttChart from './GanttChartFixed';
import ModularUnitForm from './ModularUnitForm';
import WorkCategoryForm from './WorkCategoryForm';
import TaskForm from './TaskForm';
import { t } from '../../utils/textUtils';

const GanttChartManager = ({ projectId }) => {
  const [modularUnits, setModularUnits] = useState([]);
  const [workCategories, setWorkCategories] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  // Log project ID to help with debugging
  console.log('GanttChartManager initialized with projectId:', projectId);
  
  // State for dialogs
  const [modularUnitDialog, setModularUnitDialog] = useState({
    open: false,
    modularUnit: null
  });
  const [workCategoryDialog, setWorkCategoryDialog] = useState({
    open: false,
    modularUnitId: null,
    workCategory: null
  });
  const [taskDialog, setTaskDialog] = useState({
    open: false,
    workCategoryId: null,
    task: null
  });
  
  // State for expanded sections
  const [expandedModularUnits, setExpandedModularUnits] = useState({});
  
  const { enqueueSnackbar } = useSnackbar();
  
  const fetchTasksForWorkCategory = useCallback(async (workCategoryId) => {
    try {
      console.log(`Fetching tasks for work category ${workCategoryId}`);
      const response = await api.get(`/work-categories/${workCategoryId}/tasks`);
      console.log(`Tasks response for category ${workCategoryId}:`, response.data);
      
      if (response.data && response.data.tasks) {
        console.log(`Successfully fetched ${response.data.tasks.length} tasks for category ${workCategoryId}`);
        // Store tasks somewhere if needed
      }
    } catch (error) {
      console.error(`Error fetching tasks for work category ${workCategoryId}:`, error);
    }
  }, []);

  const fetchWorkCategories = useCallback(async (modularUnitId) => {
    try {
      console.log(`Fetching work categories for modular unit ${modularUnitId}`);
      const response = await api.get(`/modular-units/${modularUnitId}/work-categories`);
      console.log(`Work categories response for unit ${modularUnitId}:`, response.data);
      
      if (response.data && response.data.workCategories) {
        console.log(`Successfully fetched work categories for unit ${modularUnitId}:`, response.data.workCategories);
        setWorkCategories(prev => ({
          ...prev,
          [modularUnitId]: response.data.workCategories
        }));
        
        // Also fetch tasks for each work category
        if (Array.isArray(response.data.workCategories)) {
          response.data.workCategories.forEach(category => {
            fetchTasksForWorkCategory(category.id);
          });
        }
      } else {
        console.error(`Unexpected work categories response format for unit ${modularUnitId}:`, response.data);
      }
    } catch (error) {
      console.error(`Error fetching work categories for modular unit ${modularUnitId}:`, error);
      enqueueSnackbar(t('GAGAL MEMUAT DATA KATEGORI PEKERJAAN'), { variant: 'error' });
    }
  }, [enqueueSnackbar, fetchTasksForWorkCategory]);
  
  const fetchModularUnits = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Fetching modular units for project:', projectId);
      const response = await api.get(`/projects/${projectId}/modular-units`);
      console.log('Modular units response:', response.data);
      
      if (response.data && response.data.modularUnits) {
        console.log('Successfully fetched modular units:', response.data.modularUnits);
        setModularUnits(response.data.modularUnits);
        
        // Initialize expanded state for new modular units
        const newExpandedState = { ...expandedModularUnits };
        response.data.modularUnits.forEach(unit => {
          if (newExpandedState[unit.id] === undefined) {
            newExpandedState[unit.id] = true; // Default to expanded
          }
        });
        setExpandedModularUnits(newExpandedState);
        
        // Fetch work categories for each modular unit
        response.data.modularUnits.forEach(unit => {
          fetchWorkCategories(unit.id);
        });
      } else {
        console.error('Unexpected modular units response format:', response.data);
        enqueueSnackbar(t('INVALID DATA FORMAT RECEIVED FROM SERVER'), { variant: 'warning' });
      }
    } catch (error) {
      console.error('Error fetching modular units:', error);
      setError(t('GAGAL MEMUAT DATA UNIT BANGUNAN. SILAKAN COBA LAGI.'));
      enqueueSnackbar(t('GAGAL MEMUAT DATA UNIT BANGUNAN'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [projectId, enqueueSnackbar, expandedModularUnits, fetchWorkCategories]);
  
  useEffect(() => {
    console.log('useEffect triggered with projectId:', projectId, 'and refreshTrigger:', refreshTrigger);
    if (projectId) {
      fetchModularUnits();
    }
  }, [projectId, refreshTrigger, fetchModularUnits]);
  
  const handleRefresh = () => {
    console.log('handleRefresh called, incrementing refreshTrigger');
    setRefreshTrigger(prev => {
      const newValue = prev + 1;
      console.log('refreshTrigger updated from', prev, 'to', newValue);
      return newValue;
    });
  };
  
  const handleToggleModularUnit = (modularUnitId) => {
    setExpandedModularUnits(prev => ({
      ...prev,
      [modularUnitId]: !prev[modularUnitId]
    }));
  };
  
  // ModularUnit handlers
  const handleAddModularUnit = () => {
    setModularUnitDialog({
      open: true,
      modularUnit: null
    });
  };
  
  const handleEditModularUnit = (modularUnit) => {
    setModularUnitDialog({
      open: true,
      modularUnit
    });
  };
  
  const handleDeleteModularUnit = async (modularUnitId) => {
    if (!window.confirm(t('APAKAH ANDA YAKIN INGIN MENGHAPUS UNIT BANGUNAN INI? SEMUA KATEGORI PEKERJAAN DAN PEKERJAAN DI DALAMNYA JUGA AKAN DIHAPUS.'))) {
      return;
    }
    
    try {
      await api.delete(`/modular-units/${modularUnitId}`);
      enqueueSnackbar(t('UNIT MODULAR BERHASIL DIHAPUS'), { variant: 'success' });
      handleRefresh();
    } catch (error) {
      console.error('Error deleting modular unit:', error);
      
      if (error.response && error.response.status === 400) {
        enqueueSnackbar(error.response.data.message || t('TIDAK DAPAT MENGHAPUS UNIT BANGUNAN YANG MEMILIKI KATEGORI PEKERJAAN'), { variant: 'error' });
      } else {
        enqueueSnackbar(t('GAGAL MENGHAPUS UNIT BANGUNAN'), { variant: 'error' });
      }
    }
  };
  
  const handleCloseModularUnitDialog = () => {
    setModularUnitDialog({
      open: false,
      modularUnit: null
    });
  };
  
  const handleSaveModularUnit = () => {
    handleRefresh();
    handleCloseModularUnitDialog();
  };
  
  // WorkCategory handlers
  const handleAddWorkCategory = (modularUnitId) => {
    setWorkCategoryDialog({
      open: true,
      modularUnitId,
      workCategory: null
    });
  };
  
  const handleEditWorkCategory = (workCategory) => {
    setWorkCategoryDialog({
      open: true,
      modularUnitId: workCategory.modularUnitId,
      workCategory
    });
  };
  
  const handleDeleteWorkCategory = async (workCategoryId) => {
    console.log('handleDeleteWorkCategory called with ID:', workCategoryId);
    
    // Use Material-UI Snackbar for confirmation instead of window.confirm
    // Kembali ke implementasi window.confirm untuk sementara
    console.log('Showing confirmation dialog');
    if (!window.confirm(t('APAKAH ANDA YAKIN INGIN MENGHAPUS KATEGORI PEKERJAAN INI? SEMUA PEKERJAAN DI DALAMNYA JUGA AKAN DIHAPUS.'))) {
      console.log('User canceled deletion');
      return;
    }
    
    console.log('User confirmed deletion');
    
    try {
      console.log('Sending delete request to API for workCategoryId:', workCategoryId, 'type:', typeof workCategoryId);
      
      // Ensure workCategoryId is a number
      const categoryId = parseInt(workCategoryId);
      console.log('Parsed categoryId:', categoryId, 'type:', typeof categoryId);
      
      if (isNaN(categoryId)) {
        console.error('Invalid workCategoryId:', workCategoryId);
        enqueueSnackbar(t('ID KATEGORI PEKERJAAN TIDAK VALID'), { variant: 'error' });
        return;
      }
      
      const response = await api.delete(`/work-categories/${categoryId}`);
      console.log('Delete API response:', response);
      
      enqueueSnackbar(t('KATEGORI PEKERJAAN BERHASIL DIHAPUS'), { variant: 'success' });
      
      console.log('Refreshing data');
      handleRefresh();
      
      // Force a second refresh after a delay
      setTimeout(() => {
        console.log('Performing second refresh');
        handleRefresh();
      }, 1000);
      
    } catch (error) {
      console.error('Error deleting work category:', error);
      
      if (error.response && error.response.status === 400) {
        enqueueSnackbar(error.response.data.message || t('TIDAK DAPAT MENGHAPUS KATEGORI PEKERJAAN YANG MEMILIKI PEKERJAAN'), { variant: 'error' });
      } else {
        enqueueSnackbar(t('GAGAL MENGHAPUS KATEGORI PEKERJAAN: ') + (error.message || t('UNKNOWN ERROR')), { variant: 'error' });
      }
    }
  };
  
  const handleCloseWorkCategoryDialog = () => {
    setWorkCategoryDialog({
      open: false,
      modularUnitId: null,
      workCategory: null
    });
  };
  
  const handleSaveWorkCategory = () => {
    handleRefresh();
    handleCloseWorkCategoryDialog();
  };
  
  // Task handlers
  const handleAddTask = (workCategoryId) => {
    setTaskDialog({
      open: true,
      workCategoryId,
      task: null
    });
  };
  
  
  const handleCloseTaskDialog = () => {
    setTaskDialog({
      open: false,
      workCategoryId: null,
      task: null
    });
  };
  
  const handleSaveTask = () => {
    handleRefresh();
    handleCloseTaskDialog();
  };
  
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">{t('STRUKTUR PEKERJAAN')}</Typography>
          <Box>
            <Button 
              variant="outlined" 
              startIcon={<RefreshIcon />} 
              onClick={handleRefresh}
              sx={{ mr: 1 }}
            >
              {t('REFRESH')}
            </Button>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={handleAddModularUnit}
            >
              {t('TAMBAH UNIT BANGUNAN BARU')}
            </Button>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        {loading && modularUnits.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : modularUnits.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('BELUM ADA UNIT BANGUNAN. KLIK "TAMBAH UNIT BANGUNAN BARU" UNTUK MEMBUAT UNIT BANGUNAN BARU.')}
          </Alert>
        ) : (
          <Box>
            {modularUnits.map(modularUnit => (
              <Paper key={modularUnit.id} elevation={1} sx={{ mb: 2, overflow: 'hidden' }}>
                <Box 
                  sx={{ 
                    p: 2, 
                    bgcolor: 'primary.main', 
                    color: 'white',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton 
                      size="small" 
                      onClick={() => handleToggleModularUnit(modularUnit.id)}
                      sx={{ color: 'white', mr: 1 }}
                    >
                      {expandedModularUnits[modularUnit.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                    <Typography variant="subtitle1">{modularUnit.name}</Typography>
                  </Box>
                  <Box>
                    <Tooltip title="Edit Unit Bangunan">
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditModularUnit(modularUnit)}
                        sx={{ color: 'white', mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Hapus Unit Bangunan">
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteModularUnit(modularUnit.id)}
                        sx={{ color: 'white' }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                
                {expandedModularUnits[modularUnit.id] && (
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle2">Kategori Pekerjaan</Typography>
                      <Button 
                        variant="outlined" 
                        size="small"
                        startIcon={<AddIcon />} 
                        onClick={() => handleAddWorkCategory(modularUnit.id)}
                      >
                        Tambah Kategori
                      </Button>
                    </Box>
                    
                    {!workCategories[modularUnit.id] ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : workCategories[modularUnit.id].length === 0 ? (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Belum ada kategori pekerjaan. Klik "Tambah Kategori" untuk membuat kategori pekerjaan baru.
                      </Alert>
                    ) : (
                      workCategories[modularUnit.id].map(workCategory => (
                        <Paper key={workCategory.id} elevation={1} sx={{ mb: 2, overflow: 'hidden' }}>
                          <Box 
                            sx={{ 
                              p: 1.5, 
                              bgcolor: 'secondary.main', 
                              color: 'white',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <Typography variant="body1">{workCategory.name}</Typography>
                            <Box>
                              <Tooltip title="Tambah Pekerjaan">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleAddTask(workCategory.id)}
                                  sx={{ color: 'white', mr: 1 }}
                                >
                                  <AddIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit Kategori">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleEditWorkCategory(workCategory)}
                                  sx={{ color: 'white', mr: 1 }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Hapus Kategori">
                                <IconButton 
                                  size="small" 
                                  onClick={() => handleDeleteWorkCategory(workCategory.id)}
                                  sx={{ color: 'white' }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Paper>
                      ))
                    )}
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        )}
      </Paper>
      
      <Box sx={{ flex: 1, minHeight: '500px' }}>
        <GanttChart 
          projectId={projectId} 
          key={`gantt-${refreshTrigger}`} 
          onAddTask={() => {
            console.log('Add task requested from GanttChart');
            // Find the first work category to add a task to
            if (modularUnits.length > 0) {
              const firstModularUnitId = modularUnits[0].id;
              const categoriesForUnit = workCategories[firstModularUnitId] || [];
              
              if (categoriesForUnit.length > 0) {
                const firstCategoryId = categoriesForUnit[0].id;
                console.log(`Opening task dialog for category ${firstCategoryId}`);
                setTaskDialog({
                  open: true,
                  workCategoryId: firstCategoryId,
                  task: null
                });
              } else {
                // No categories, prompt to create one
                enqueueSnackbar('Buat kategori pekerjaan terlebih dahulu', { variant: 'info' });
                setWorkCategoryDialog({
                  open: true,
                  modularUnitId: firstModularUnitId,
                  workCategory: null
                });
              }
            } else {
              // No modular units, prompt to create one
              enqueueSnackbar('Buat unit bangunan terlebih dahulu', { variant: 'info' });
              setModularUnitDialog({
                open: true,
                modularUnit: null
              });
            }
          }}
          onEditTask={(task) => {
            console.log('🎯 Edit task requested from GanttChart:', task);
            console.log('🎯 Task workCategoryId:', task?.workCategoryId);
            if (task && task.workCategoryId) {
              console.log('🎯 Opening TaskDialog for edit...');
              setTaskDialog({
                open: true,
                workCategoryId: task.workCategoryId,
                task: task
              });
            } else {
              console.warn('🎯 Cannot edit task - missing workCategoryId');
            }
          }}
          onRefresh={handleRefresh}
        />
      </Box>
      
      {/* Dialogs */}
      <ModularUnitForm
        open={modularUnitDialog.open}
        onClose={handleCloseModularUnitDialog}
        projectId={projectId}
        modularUnit={modularUnitDialog.modularUnit}
        onSave={handleSaveModularUnit}
      />
      
      <WorkCategoryForm
        open={workCategoryDialog.open}
        onClose={handleCloseWorkCategoryDialog}
        modularUnitId={workCategoryDialog.modularUnitId}
        workCategory={workCategoryDialog.workCategory}
        onSave={handleSaveWorkCategory}
      />
      
      <TaskForm
        open={taskDialog.open}
        onClose={handleCloseTaskDialog}
        workCategoryId={taskDialog.workCategoryId}
        task={taskDialog.task}
        projectId={projectId}
        onSave={handleSaveTask}
      />
    </Box>
  );
};

export default GanttChartManager;
